import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronDown, Loader2, Lock, Search, X } from "lucide-react";
import { api } from "@/lib/api";

// Rutas de captura de un trámite: mientras se llena no debe poder cambiarse la
// empresa (dejaría la captura en curso inconsistente). Cubre las altas de
// solicitud de cualquier sector, el alta de registro legal y las pantallas
// dedicadas de los trámites Telecom (excepto la selección de certificado, donde
// sí conviene poder cambiar de empresa). Por patrón, abarca servicios futuros.
function esRutaCaptura(pathname: string): boolean {
  return (
    /\/solicitudes\/nueva\/?$/.test(pathname) ||
    pathname.startsWith("/tramites-legales/alta") ||
    (pathname.startsWith("/telecom/servicios/") &&
      !pathname.startsWith("/telecom/servicios/seleccionar-certificado"))
  );
}

/**
 * Selector de contexto de actuación: lista los RLs en los que el usuario
 * puede actuar (asociaciones legales vigentes vía carta poder o
 * representación directa del titular). Persiste la selección en
 * sessionStorage bajo "sicen.contexto.titularId" para que otros componentes
 * y futuras llamadas la consulten.
 *
 * Auto-selecciona cuando hay exactamente 1 contexto. Si la lista está
 * vacía muestra "Sin contextos disponibles" (sin dropdown).
 */
interface Contexto {
  asociacionId: string | null;
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  tipoAutorizacion: string;
  /** Backend devuelve `sectoresCodigos` (DTO ContextoActuacion). */
  sectoresCodigos: string[];
  vigenciaFin: string | null;
  cartaSinVencimiento: boolean | null;
}

const STORAGE_KEY = "sicen.contexto.titularId";

/** Umbral a partir del cual se muestra el buscador dentro del dropdown. */
const FILTRO_MIN = 5;

export function ContextSelector() {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const bloqueadoPorCaptura = esRutaCaptura(pathname);
  const [open, setOpen] = useState(false);
  const [seleccionado, setSeleccionado] = useState<string | null>(
    () => window.sessionStorage.getItem(STORAGE_KEY),
  );
  const [filtro, setFiltro] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Si se navega a una pantalla de captura con el dropdown abierto, ciérralo.
  useEffect(() => {
    if (bloqueadoPorCaptura) setOpen(false);
  }, [bloqueadoPorCaptura]);

  const q = useQuery<Contexto[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<Contexto[]>("/api/contextos/mios")).data,
    staleTime: 30_000,
  });

  // Si no hay selección persistida y llega exactamente 1 contexto, lo auto-selecciona.
  useEffect(() => {
    if (!seleccionado && q.data && q.data.length === 1) {
      setSeleccionado(q.data[0].titularId);
      window.sessionStorage.setItem(STORAGE_KEY, q.data[0].titularId);
    }
  }, [q.data, seleccionado]);

  // Cierra al click fuera.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Al abrir: limpia filtro y enfoca buscador (si aplica).
  useEffect(() => {
    if (!open) {
      setFiltro("");
      return;
    }
    const t = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [open]);

  const contextos = q.data ?? [];
  const actual = contextos.find((c) => c.titularId === seleccionado) ?? null;
  const mostrarBuscador = contextos.length >= FILTRO_MIN;

  const contextosFiltrados = useMemo(() => {
    if (!filtro.trim()) return contextos;
    const f = filtro.trim().toLowerCase();
    return contextos.filter((c) =>
      c.titularRazonSocial.toLowerCase().includes(f) ||
      (c.tramitadorRazonSocial?.toLowerCase().includes(f) ?? false),
    );
  }, [contextos, filtro]);

  const elegir = (c: Contexto) => {
    setSeleccionado(c.titularId);
    window.sessionStorage.setItem(STORAGE_KEY, c.titularId);
    setOpen(false);
    // El contexto activo viaja en el header X-Sicen-Titular-Id; al cambiarlo
    // hay que invalidar las queries para que los listados dependientes del
    // contexto (certificados vigentes, solicitudes, etc.) se refresquen en vez
    // de servir la caché de la empresa anterior.
    qc.invalidateQueries();
  };

  if (q.isLoading) {
    return (
      <span className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-surface/10 px-3 py-1.5 text-sm text-white/70">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando contextos…
      </span>
    );
  }

  if (contextos.length === 0) {
    return (
      <span className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-surface/10 px-3 py-1.5 text-sm text-white/70">
        <Building2 className="h-4 w-4" />
        Sin contextos disponibles
      </span>
    );
  }

  return (
    <div ref={dropdownRef} className="relative w-full sm:w-auto">
      <button
        type="button"
        onClick={() => !bloqueadoPorCaptura && setOpen((v) => !v)}
        disabled={bloqueadoPorCaptura}
        title={bloqueadoPorCaptura ? "Termina o cancela la solicitud para cambiar de empresa." : undefined}
        className={`flex max-w-full items-center gap-2 overflow-hidden rounded-md border border-white/20 bg-surface/10 px-3 py-1.5 text-sm sm:max-w-none ${
          bloqueadoPorCaptura ? "cursor-not-allowed opacity-70" : "hover:bg-surface/15"
        }`}
      >
        <Building2 className="h-4 w-4 shrink-0" />
        {actual ? (
          <>
            <span className="min-w-0 truncate font-medium">{actual.titularRazonSocial}</span>
            <span className="hidden shrink-0 text-white/60 sm:inline">·</span>
            <span className="hidden shrink-0 text-white/80 sm:inline">{labelTipo(actual.tipoAutorizacion)}</span>
          </>
        ) : (
          <>
            <span className="min-w-0 truncate font-medium">Selecciona empresa</span>
            <span className="hidden shrink-0 text-white/60 sm:inline">·</span>
            <span className="hidden shrink-0 text-white/80 sm:inline">
              {contextos.length} disponible{contextos.length === 1 ? "" : "s"}
            </span>
          </>
        )}
        {bloqueadoPorCaptura ? (
          <Lock className="ml-auto h-4 w-4 shrink-0 text-white/60 sm:ml-0" />
        ) : (
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-white/60 sm:ml-0" />
        )}
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-md border bg-surface text-fg shadow-overlay sm:left-1/2 sm:right-auto sm:w-[420px] sm:-translate-x-1/2">
          {mostrarBuscador && (
            <div className="border-b p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-faint" />
                <input
                  ref={searchRef}
                  type="text"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      if (filtro) setFiltro("");
                      else setOpen(false);
                    } else if (e.key === "Enter" && contextosFiltrados.length === 1) {
                      elegir(contextosFiltrados[0]);
                    }
                  }}
                  placeholder="Buscar empresa…"
                  className="w-full rounded border border-edge-strong py-1.5 pl-8 pr-7 text-sm focus:border-primary focus:outline-none"
                />
                {filtro && (
                  <button
                    type="button"
                    onClick={() => {
                      setFiltro("");
                      searchRef.current?.focus();
                    }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-fg-faint hover:bg-surface-2 hover:text-fg-muted"
                    aria-label="Limpiar búsqueda"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
          <ul className="max-h-80 overflow-y-auto py-1">
            {contextosFiltrados.length === 0 ? (
              <li className="px-4 py-3 text-center text-xs text-fg-subtle">
                No hay empresas que coincidan con "{filtro}".
              </li>
            ) : (
              contextosFiltrados.map((c) => {
                const activo = c.titularId === seleccionado;
                return (
                  <li key={`${c.titularId}-${c.asociacionId ?? "directo"}`}>
                    <button
                      type="button"
                      onClick={() => elegir(c)}
                      className={`flex w-full items-start gap-3 px-4 py-2 text-left text-sm hover:bg-surface-2 ${
                        activo ? "bg-estado-info-bg" : ""
                      }`}
                    >
                      <span className="mt-0.5">
                        {activo ? (
                          <Check className="h-4 w-4 text-primary-strong" />
                        ) : (
                          <span className="block h-4 w-4" />
                        )}
                      </span>
                      <span className="flex-1">
                        <span className="block font-medium text-fg">{c.titularRazonSocial}</span>
                        <span className="mt-0.5 flex flex-wrap gap-1 text-[11px] text-fg-muted">
                          <BadgeTipo tipo={c.tipoAutorizacion} />
                          {(c.sectoresCodigos ?? []).map((s) => (
                            <span key={s} className="rounded bg-surface-2 px-1.5 py-0.5 font-mono">
                              {s}
                            </span>
                          ))}
                        </span>
                        {c.tramitadorRazonSocial && (
                          <span className="mt-0.5 block text-[11px] text-fg-subtle">
                            vía tramitador {c.tramitadorRazonSocial}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function labelTipo(tipo: string): string {
  return (
    {
      REPRESENTANTE_DIRECTO: "Representante",
      INTEGRAL: "Tramitador integral",
      RESTRINGIDA: "Tramitador restringido",
      OPERATIVA: "Tramitador operativo",
      INTERNO_TITULAR: "Autorizado interno",
      GESTION: "Autorizado (gestión)",
      CONSULTA: "Autorizado (consulta)",
      SEGUIMIENTO: "Autorizado (seguimiento)",
      FIRMA: "Autorizado (firma)",
    }[tipo] ?? tipo
  );
}

function BadgeTipo({ tipo }: { tipo: string }) {
  const cls =
    tipo === "REPRESENTANTE_DIRECTO"
      ? "bg-estado-info-bg text-estado-info"
      : "bg-estado-warn-bg text-estado-warn";
  return <span className={`rounded px-1.5 py-0.5 font-medium ${cls}`}>{labelTipo(tipo)}</span>;
}
